import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";
import { useRestaurant } from "./RestaurantContext";

interface QRCode {
  id: number;
  code: string;
  restaurantId: number;
  targetUrl: string;
  createdAt: string;
  active: boolean;
}

interface OptIn {
  id: number;
  email: string | null;
  phone: string | null;
  consent: boolean;
  timestamp: string;
}

const OPTINS_PAGE_SIZE = 10;

const CampaignAnalytics: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { currentRestaurantId, loading: restaurantLoading } = useRestaurant();
  const [qrCode, setQrCode] = useState<QRCode | null>(null);
  const [qrLoading, setQrLoading] = useState(true);
  const [optIns, setOptIns] = useState<OptIn[]>([]);
  const [optInsTotal, setOptInsTotal] = useState(0);
  // Total across ALL opt-ins, independent of the search filter. Only
  // updated from an unfiltered fetch, so a search that matches nothing
  // doesn't wrongly disable "Export CSV" (which always exports everything)
  // or hide the search box itself.
  const [optInsGrandTotal, setOptInsGrandTotal] = useState(0);
  const [optInsPage, setOptInsPage] = useState(1);
  const [optInsSearchInput, setOptInsSearchInput] = useState('');
  const [optInsSearch, setOptInsSearch] = useState('');
  const [optInsLoading, setOptInsLoading] = useState(false);
  const [optInsError, setOptInsError] = useState<string | null>(null);
  const [deletingOptInId, setDeletingOptInId] = useState<number | null>(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [scanStats, setScanStats] = useState<{ totalScans: number; lastScan: string | null; sponsorClicks: number } | null>(null);
  const [scanStatsLoading, setScanStatsLoading] = useState(false);
  const [scanStatsError, setScanStatsError] = useState<string | null>(null);
  const [detailedScans, setDetailedScans] = useState<any[]>([]);
  const [detailedScansLoading, setDetailedScansLoading] = useState(false);
  const [areasRangeDays, setAreasRangeDays] = useState<7 | 30 | 90>(30);
  const [areasData, setAreasData] = useState<{ total: number; located: number; areas: { country: string | null; region: string | null; city: string | null; count: number }[] } | null>(null);
  const [areasLoading, setAreasLoading] = useState(false);
  const [areasError, setAreasError] = useState<string | null>(null);

  // Waits for RestaurantContext's own fetch, but only when there's truly no
  // id yet - see the matching comment in Reviews.tsx. A returning user
  // already has currentRestaurantId from localStorage synchronously, so
  // there's no reason to also wait out the full restaurants-list fetch. A
  // confirmed "no restaurant at all" (loading finished, still no id) still
  // stops here exactly as before.
  useEffect(() => {
    if (!currentRestaurantId) {
      if (restaurantLoading) return;
      setQrLoading(false);
      return;
    }
    setQrLoading(true);
    apiFetch(`/api/restaurants/${currentRestaurantId}/qr`)
      .then(res => (res && res.ok ? res.json() : null))
      .then(data => setQrCode(data))
      .catch(() => setQrCode(null))
      .finally(() => setQrLoading(false));
  }, [currentRestaurantId, restaurantLoading]);

  useEffect(() => {
    if (!qrCode?.code) return;
    setDetailedScansLoading(true);
    apiFetch(`/api/qr/${qrCode.code}/scans`)
      .then(res => (res && res.ok ? res.json() : Promise.reject(res)))
      .then(scans => setDetailedScans(scans))
      .catch(err => console.error('Failed to fetch detailed scans:', err))
      .finally(() => setDetailedScansLoading(false));
  }, [qrCode]);

  // Reset to page 1 whenever a different QR code is loaded (defensive: in
  // practice a restaurant has exactly one), so a stale page number from a
  // previous code's list can't produce an out-of-range page here.
  useEffect(() => {
    setOptInsPage(1);
  }, [qrCode?.code]);

  // Debounced so typing a search doesn't fire a request per keystroke; 300ms
  // is short enough to feel live without hammering the API on every letter.
  useEffect(() => {
    const timer = setTimeout(() => setOptInsSearch(optInsSearchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [optInsSearchInput]);

  // A new search term invalidates whatever page number was showing: page 3
  // of the full list is meaningless once the list is filtered.
  useEffect(() => {
    setOptInsPage(1);
  }, [optInsSearch]);

  const fetchOptIns = useCallback(() => {
    if (!qrCode?.code) return;
    setOptInsLoading(true);
    setOptInsError(null);
    const params = new URLSearchParams({ page: String(optInsPage) });
    if (optInsSearch) params.set('q', optInsSearch);
    apiFetch(`/api/qr/${qrCode.code}/optins?${params}`)
      .then(res => (res && res.ok ? res.json() : Promise.reject(res)))
      .then(data => {
        setOptIns(data.items);
        setOptInsTotal(data.total);
        if (!optInsSearch) setOptInsGrandTotal(data.total);
      })
      .catch(() => setOptInsError(t('restaurant.failedOptIns')))
      .finally(() => setOptInsLoading(false));
  }, [qrCode, optInsPage, optInsSearch, t]);

  useEffect(() => {
    fetchOptIns();
  }, [fetchOptIns]);

  const handleDeleteOptIn = async (id: number) => {
    if (!qrCode?.code) return;
    if (!window.confirm(t('restaurant.confirmDeleteOptIn'))) return;
    setDeletingOptInId(id);
    try {
      const res = await apiFetch(`/api/qr/${qrCode.code}/optins/${id}`, { method: 'DELETE' });
      if (res && res.ok) {
        // Stepping back a page (rather than refetching the now-empty current
        // one) only matters when this was the last row on a page beyond the
        // first; the page-change effect above re-fetches either way.
        if (optIns.length === 1 && optInsPage > 1) {
          setOptInsPage(p => p - 1);
        } else {
          fetchOptIns();
        }
      } else {
        setOptInsError(t('restaurant.failedDeleteOptIn'));
      }
    } catch {
      setOptInsError(t('restaurant.failedDeleteOptIn'));
    } finally {
      setDeletingOptInId(null);
    }
  };

  const handleExportCsv = async () => {
    if (!qrCode?.code) return;
    setExportingCsv(true);
    setOptInsError(null);
    try {
      const res = await apiFetch(`/api/qr/${qrCode.code}/optins/export`);
      if (!res || !res.ok) throw new Error('export failed');
      const blob = await res.blob();
      // A blob: URL, not a direct link to api.reviewcounter.ca: the app and
      // API are different origins, and a cross-origin <a download> doesn't
      // reliably force a save, so this way the browser always downloads it.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `optins-${qrCode.code}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setOptInsError(t('restaurant.failedExportCsv'));
    } finally {
      setExportingCsv(false);
    }
  };

  useEffect(() => {
    if (qrCode?.code) {
      setScanStatsLoading(true);
      setScanStatsError(null);
      apiFetch(`/api/qr/${qrCode.code}/stats`)
        .then(res => (res && res.ok ? res.json() : Promise.reject(res)))
        .then(data => setScanStats(data))
        .catch(() => setScanStatsError(t('restaurant.failedScanStats')))
        .finally(() => setScanStatsLoading(false));
    }
  }, [qrCode, t]);

  useEffect(() => {
    if (qrCode?.code) {
      setAreasLoading(true);
      setAreasError(null);
      // Full ISO timestamps built here, in the browser, so the range means the
      // owner's local days rather than UTC ones (matches the backend's read of "to"/"from").
      const to = new Date();
      const from = new Date(to.getTime() - areasRangeDays * 86400000);
      const params = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
      apiFetch(`/api/qr/${qrCode.code}/areas?${params}`)
        .then(res => (res && res.ok ? res.json() : Promise.reject(res)))
        .then(data => setAreasData(data))
        .catch(() => setAreasError(t('restaurant.areas.failed')))
        .finally(() => setAreasLoading(false));
    }
  }, [qrCode, areasRangeDays, t]);

  if (restaurantLoading || qrLoading) {
    return <div className="p-6 text-center text-gray-500">{t('restaurant.loadingQr')}</div>;
  }

  if (!qrCode) {
    return (
      <div className="p-6 max-w-md mx-auto text-center">
        <h1 className="text-2xl font-bold mb-4">{t('restaurant.campaignAnalyticsTitle')}</h1>
        <p className="text-gray-500 mb-4">{t('restaurant.campaignAnalyticsNoQr')}</p>
        <Link to="/restaurant" className="text-teal-600 hover:underline">
          {t('restaurant.campaignAnalyticsGoCreate')}
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-center">{t('restaurant.campaignAnalyticsTitle')}</h1>
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Scan Stats */}
        <div>
          <h3 className="font-semibold mb-2">{t('restaurant.scanStats')}</h3>
          {scanStatsLoading ? (
            <div className="text-gray-500">{t('restaurant.loadingScanStats')}</div>
          ) : scanStatsError ? (
            <div className="text-red-600">{scanStatsError}</div>
          ) : scanStats ? (
            <>
              <div className="text-gray-600">{t('restaurant.totalScans', { count: scanStats.totalScans })}</div>
              <div className="text-gray-600">{t('restaurant.lastScan', { value: scanStats.lastScan ? new Date(scanStats.lastScan).toLocaleString() : '--' })}</div>
              <div className="text-gray-600">{t('restaurant.sponsorClicks', { count: scanStats.sponsorClicks })}</div>
            </>
          ) : (
            <div className="text-gray-400">{t('restaurant.noScanData')}</div>
          )}
        </div>
        {/* Opt-in List */}
        <div>
          <h3 className="font-semibold mb-2">{t('restaurant.optInList')}</h3>
          {optInsError && <div className="text-red-600 mb-2">{optInsError}</div>}
          {(optInsGrandTotal > 0 || optInsSearchInput) && (
            <input
              type="search"
              value={optInsSearchInput}
              onChange={e => setOptInsSearchInput(e.target.value)}
              placeholder={t('restaurant.optInsSearchPlaceholder')}
              className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
            />
          )}
          {optInsLoading ? (
            <div className="text-gray-500">{t('restaurant.loadingOptIns')}</div>
          ) : optIns.length === 0 ? (
            <div className="text-gray-400">
              {optInsSearch ? t('restaurant.noOptInsMatch') : t('restaurant.noOptIns')}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border rounded">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 border">{t('common.email')}</th>
                      <th className="px-2 py-1 border">{t('common.phone')}</th>
                      <th className="px-2 py-1 border">{t('common.date')}</th>
                      <th className="px-2 py-1 border"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {optIns.map((opt) => (
                      <tr key={opt.id}>
                        <td className="px-2 py-1 border">{opt.email || '-'}</td>
                        <td className="px-2 py-1 border">{opt.phone || '-'}</td>
                        <td className="px-2 py-1 border">{opt.timestamp ? new Date(opt.timestamp).toLocaleString() : '-'}</td>
                        <td className="px-2 py-1 border text-right">
                          <button
                            type="button"
                            onClick={() => handleDeleteOptIn(opt.id)}
                            disabled={deletingOptInId === opt.id}
                            className="text-xs text-red-600 hover:underline disabled:opacity-50"
                          >
                            {deletingOptInId === opt.id ? t('restaurant.deletingOptIn') : t('restaurant.deleteOptIn')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {optInsTotal > OPTINS_PAGE_SIZE && (
                <div className="flex items-center justify-between mt-2 text-sm">
                  <button
                    type="button"
                    onClick={() => setOptInsPage(p => Math.max(1, p - 1))}
                    disabled={optInsPage <= 1}
                    className="px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  >
                    {t('restaurant.optInsPrev')}
                  </button>
                  <span className="text-gray-500">
                    {t('restaurant.optInsPageOf', {
                      page: optInsPage,
                      total: Math.max(1, Math.ceil(optInsTotal / OPTINS_PAGE_SIZE)),
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => setOptInsPage(p => (p * OPTINS_PAGE_SIZE < optInsTotal ? p + 1 : p))}
                    disabled={optInsPage * OPTINS_PAGE_SIZE >= optInsTotal}
                    className="px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  >
                    {t('restaurant.optInsNext')}
                  </button>
                </div>
              )}
            </>
          )}
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exportingCsv || optInsGrandTotal === 0}
            className="mt-2 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            {exportingCsv ? t('restaurant.exportingCsv') : t('restaurant.exportCsv')}
          </button>
        </div>
        {/* Heatmap */}
        <div>
          <h3 className="font-semibold mb-2">{t('restaurant.scanHeatmap')}</h3>
          {detailedScansLoading ? (
            <div className="text-gray-500">{t('restaurant.loadingHeatmap')}</div>
          ) : detailedScans.length > 0 ? (
            <div className="w-full h-32 bg-stone-50 rounded border p-2">
              <div className="grid grid-cols-7 gap-1 h-full">
                {Array.from({ length: 7 }, (_, i) => {
                  const date = new Date();
                  date.setDate(date.getDate() - (6 - i));
                  const dayName = date.toLocaleDateString(i18n.language, { weekday: 'short' });
                  const dayStart = new Date(date);
                  dayStart.setHours(0, 0, 0, 0);
                  const dayEnd = new Date(date);
                  dayEnd.setHours(23, 59, 59, 999);

                  // Count scans for this specific day
                  const dayScans = detailedScans.filter(scan => {
                    const scanDate = new Date(scan.timestamp);
                    return scanDate >= dayStart && scanDate <= dayEnd;
                  }).length;

                  const intensity = Math.min(dayScans * 25, 100);
                  return (
                    <div key={i} className="flex flex-col items-center justify-end">
                      <div className="flex flex-col items-center mb-1">
                        <div
                          className="w-full rounded-sm"
                          style={{
                            height: `${Math.max(dayScans * 6, 4)}px`,
                            backgroundColor: `rgba(59, 130, 246, ${intensity / 100})`
                          }}
                          title={`${dayScans} scans on ${dayName}`}
                        />
                        {dayScans > 0 && (
                          <span className="text-xs text-teal-600 font-medium mt-1">
                            {dayScans === 1
                              ? t('restaurant.scanCountOne', { count: dayScans })
                              : t('restaurant.scanCountOther', { count: dayScans })}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{dayName}</span>
                    </div>
                  );
                })}
              </div>
              <div className="text-xs text-gray-400 mt-2 text-center">
                {t('restaurant.heatmapCaption')}
              </div>
            </div>
          ) : (
            <div className="w-full h-32 bg-gray-100 flex items-center justify-center text-gray-400 rounded">
              {t('restaurant.noScanDataYet')}
            </div>
          )}
        </div>
        {/* Scans by area */}
        <div>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <h3 className="font-semibold">{t('restaurant.areas.title')}</h3>
            <div className="flex gap-1">
              {([7, 30, 90] as const).map((days) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => setAreasRangeDays(days)}
                  className={`px-2 py-1 text-xs rounded border ${areasRangeDays === days ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                >
                  {t(`restaurant.areas.last${days}` as 'restaurant.areas.last7' | 'restaurant.areas.last30' | 'restaurant.areas.last90')}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-2">{t('restaurant.areas.caveat')}</p>
          {areasLoading ? (
            <div className="text-gray-500">{t('restaurant.loadingHeatmap')}</div>
          ) : areasError ? (
            <div className="text-red-500 text-sm">{areasError}</div>
          ) : areasData && areasData.areas.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border rounded">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 border text-left">{t('restaurant.areas.colArea')}</th>
                      <th className="px-2 py-1 border text-right">{t('restaurant.areas.colScans')}</th>
                      <th className="px-2 py-1 border text-right">{t('restaurant.areas.colShare')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areasData.areas.map((a, i) => {
                      const label = [a.city, a.region, a.country].filter(Boolean).join(', ') || t('restaurant.areas.unknown');
                      const share = areasData.total > 0 ? Math.round((a.count / areasData.total) * 100) : 0;
                      return (
                        <tr key={i}>
                          <td className="px-2 py-1 border">{label}</td>
                          <td className="px-2 py-1 border text-right">{a.count}</td>
                          <td className="px-2 py-1 border text-right">{share}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-gray-400 mt-2">
                {t('restaurant.areas.located', { located: areasData.located, total: areasData.total })}
              </div>
            </>
          ) : (
            <div className="w-full h-16 bg-gray-100 flex items-center justify-center text-gray-400 rounded">
              {t('restaurant.areas.empty')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignAnalytics;
